/**
 * A button that stays hoverable and focusable while it is unavailable, so its reason can be read (decision P3:
 * "disabled with a visible reason, never hidden"; the Table Explorer Edit Mode pattern). It never carries the native
 * `disabled` attribute — shadcn's Button is `disabled:pointer-events-none`, so a `title` on it could never show and a
 * disabled button takes no focus — but `aria-disabled`, the reason as its `title` and `data-disabled-reason`, muted
 * styling, and a click that does nothing (a submit button becomes a plain button). A native `title` shows on hover only,
 * so KEYBOARD focus also opens a tooltip with the reason (S7 review E), which is then the button's accessible
 * description; a focus that comes from the pointer opens none (the hover shows the `title`), blur or Escape closes it.
 * Without a reason it is the plain Button, whose `disabled` still covers transient states such as "saving…".
 */
import * as React from 'react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

/** `side`: where the keyboard reason opens (default below — U34); the Transfers drawer's row actions open it to the right
 *  so it never covers the row it explains (last UI/UX pass, F16). Radix still flips it when there is no room. */
export type ReasonButtonProps = React.ComponentProps<typeof Button> & { reason?: string; side?: 'top' | 'right' | 'bottom' | 'left' };

export const ReasonButton = React.forwardRef<HTMLButtonElement, ReasonButtonProps>(({ reason, side = 'bottom', className, onClick, disabled, type, title, ...props }, ref) => {
  const [keyboardFocus, setKeyboardFocus] = React.useState(false);
  const pointerDown = React.useRef(false);
  if (!reason) return <Button ref={ref} className={className} onClick={onClick} disabled={disabled} type={type} title={title} {...props} />;
  return (
    <TooltipProvider delayDuration={0}>
      {/* Radix closes on blur, Escape and click (`onOpenChange(false)`); its hover open is ignored — the hover shows the `title`. */}
      <Tooltip open={keyboardFocus} onOpenChange={(open) => { if (!open) setKeyboardFocus(false); }}>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            {...props}
            type="button"
            aria-disabled="true"
            title={reason}
            data-disabled-reason={reason}
            className={cn(className, 'cursor-not-allowed opacity-50')}
            onClick={(event) => event.preventDefault()}
            onPointerDown={(event) => { pointerDown.current = true; props.onPointerDown?.(event); }}
            onFocus={(event) => { setKeyboardFocus(!pointerDown.current); pointerDown.current = false; props.onFocus?.(event); }}
          />
        </TooltipTrigger>
        {/* Round B U34: below the control — a toolbar reason opening upward covered the tab strip (Radix flips on collision). */}
        <TooltipContent side={side}>{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
ReasonButton.displayName = 'ReasonButton';
